import React, { useContext, useEffect, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import Alert from 'react-bootstrap/Alert';
import { useDispatch } from 'react-redux';
import {
  postAbandonedCartFollowed,
  postBuyCourseWithStripe,
  postBuyGiftCardWithStripe,
  postCourseCheckoutPromocode,
} from '../../../../../backend-api';
import { loadCoursesCheckoutInformation } from '../../redux/actions';
import { loadGiftCardCheckoutInformation } from '../../../gift-card-checkout/redux/actions';
import { Loader } from '../../../../../components';
import Storage from '../../../../../storage';
import { t } from '../../../../../i18n';
import { useStripeContext } from '../../components/stripe-provider';
import useCurrentUser from '../../../../current-user/useCurrentUser';
import CheckoutForm from '../../components/checkout-form';
import RegisterMiniForm from '../../components/register-mini-form';
import CheckoutContext from '../../checkout-context';
import UrlTo from '../../../../../url-to';
import { createLogger } from '../../../../../utilities/log';
import './styles.scss';

const logger = createLogger('Courses/PaymentScreen');

function grabUserInfoFromUrlIfExists() {
  const query = new URLSearchParams(window.location.search);
  const email = query.get('email');
  const name = query.get('name');
  if (!email || !name) {
    return null;
  }
  return { email, name };
}

const PaymentScreen = ({ match, history }) => {
  const courseId = match.params.id;
  const { currentUser, isAuthenticated } = useCurrentUser();
  const [clientSecret, setClientSecret] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [anonymousUserInfo, setAnonymousUserInfo] = useState(grabUserInfoFromUrlIfExists);
  const stripe = useStripeContext();
  const dispatch = useDispatch();
  const { swish, isCoursePayment } = useContext(CheckoutContext);

  const autoApplyPromocode = Storage.courses.promocode;

  const userInfo = isAuthenticated ? currentUser : anonymousUserInfo;
  useEffect(() => {
    if (!userInfo) {
      return;
    }

    const beginStripePayment = async () => {
      try {
        const timeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const { clientSecret } = isCoursePayment
          ? await postBuyCourseWithStripe({ courseId, email: userInfo.email, name: userInfo.name })
          : await postBuyGiftCardWithStripe({ email: userInfo.email, name: userInfo.name, timeZoneName });
        setClientSecret(clientSecret);

        if (isCoursePayment && autoApplyPromocode) {
          await postCourseCheckoutPromocode({ courseId, email: userInfo.email, promocode: autoApplyPromocode });
        }
      } catch (ex) {
        const serverErrorCode = ex.response?.data.error;
        const errorMessageCode = serverErrorCode ? `DIGITAL_COURSES.CHECKOUT.ERROR.${serverErrorCode}` : 'SERVER_ERROR';
        setErrorCode(errorMessageCode);
        logger.error('Failed to begin stripe payment', {
          message: ex.message,
          serverErrorCode,
          courseId,
          isCoursePayment,
          userInfo,
        });
      }
    };

    beginStripePayment();
  }, [userInfo]);

  useEffect(() => {
    const currency = swish ? 'sek' : null;
    const loadPurchasePreviewAction = isCoursePayment
      ? loadCoursesCheckoutInformation(courseId, { email: userInfo?.email, currency, promocode: autoApplyPromocode })
      : loadGiftCardCheckoutInformation({ currency });
    dispatch(loadPurchasePreviewAction);
  }, [userInfo?.email, swish]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const abandonedCartId = query.get('abandonedCartId');
    if (abandonedCartId) {
      postAbandonedCartFollowed(abandonedCartId);
    }
  }, []);

  const handleEnterRegistrationInfo = ({ email, name, promocode }) => {
    // Save entered info into URL, so it's there if user navigates back
    const newPath = isCoursePayment
      ? UrlTo.coursesCheckout(courseId, { email, name, promocode }) // FIXME: promocode parameter is unused
      : UrlTo.coursesGiftCardCheckout({ email, name });

    if (promocode) {
      // FIXME: Shouldn't store new promocode in storage - use URL query instead
      Storage.courses.promocode = promocode;
    }

    history.replace(newPath);
    setAnonymousUserInfo({ email, name });
  };

  const appearance = {
    theme: 'stripe',
  };
  const options = {
    clientSecret,
    appearance,
  };

  const needToEnterRegisterData = !isAuthenticated && !anonymousUserInfo;
  const isPaymentIntentReady = !!stripe && clientSecret;
  return (
    <div className="courses-checkout-payment">
      <h2>{t('DIGITAL_COURSES.CHECKOUT.PAYMENT_TITLE')}</h2>
      {needToEnterRegisterData ? (
        <RegisterMiniForm courseId={courseId} onEnterData={handleEnterRegistrationInfo} />
      ) : isPaymentIntentReady ? (
        <Elements options={options} stripe={stripe}>
          <CheckoutForm
            courseId={courseId}
            email={userInfo.email}
            name={userInfo.name}
            hidePromocodeInput={!isCoursePayment || !!autoApplyPromocode}
          />
        </Elements>
      ) : !!errorCode ? (
        <Alert variant="danger">{t(errorCode)}</Alert>
      ) : (
        <Loader />
      )}
    </div>
  );
};

export default PaymentScreen;
